import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Popconfirm, Space, Typography } from 'antd';
import {
  queryMemberPlanAnalysis,
  submitMemberPlanAnalysis,
} from '@/services/technical';
import { newRetryRequestId } from './FactSyncRetryButton';
import { processingTime } from './CommunicationProcessingPanel';
import {
  PLAN_DIMENSIONS,
  PLAN_FINDING_STATES,
  PLAN_TASK_STATES,
  readPlanAnalysis,
} from './planAnalysis';
import styles from './SimpleWorkspace.less';

export default function CurrentPlanAnalysisPanel({
  memberId,
  planId,
  courseType,
  courseId,
}) {
  const whole = courseType === 0;
  const identity = `${memberId}:${planId}:${courseType}:${courseId}`;
  const generation = useRef(0);
  const busy = useRef(false);
  const command = useRef(null);
  const [state, setState] = useState({});
  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    command.current = null;
    setState({ identity });
    return () => {
      generation.current += 1;
    };
  }, [identity]);
  const current = state.identity === identity ? state : {};
  const query = { memberId: Number(memberId), planId, courseType, courseId };
  const load = async (latest = false) => {
    if (busy.current) return;
    busy.current = true;
    const version = generation.current;
    setState((s) => ({ ...s, identity, loading: true, error: null }));
    try {
      const q = {
        ...query,
        ...(!latest && current.data?.taskId
          ? { taskId: current.data.taskId }
          : {}),
      };
      const data = readPlanAnalysis(
        await queryMemberPlanAnalysis({ requestData: q }),
        q,
      );
      if (version === generation.current)
        setState((s) => ({ ...s, identity, loading: false, data }));
    } catch (error) {
      if (version === generation.current)
        setState((s) => ({ ...s, loading: false, error: message(error) }));
    } finally {
      if (version === generation.current) busy.current = false;
    }
  };
  const submit = async () => {
    if (
      busy.current ||
      (!current.uncertain &&
        !['not_found', 'succeeded', 'failed'].includes(current.data?.status)) ||
      (current.data?.status === 'failed' && current.data.nextExecuteAt)
    )
      return;
    busy.current = true;
    const version = generation.current;
    setState((s) => ({ ...s, identity, loading: true, error: null }));
    try {
      if (!command.current)
        command.current = { ...query, requestId: newRetryRequestId() };
      const data = readPlanAnalysis(
        await submitMemberPlanAnalysis({ requestData: command.current }),
        command.current,
        true,
      );
      if (version === generation.current) {
        command.current = null;
        setState({ identity, data, loading: false });
      }
    } catch (error) {
      if (version === generation.current)
        setState((s) => ({
          ...s,
          loading: false,
          uncertain: !!command.current,
          error: message(error),
        }));
    } finally {
      if (version === generation.current) busy.current = false;
    }
  };
  const data = current.data;
  const eligible =
    current.uncertain ||
    ['not_found', 'succeeded', 'failed'].includes(data?.status);
  const retryPending = data?.status === 'failed' && data.nextExecuteAt;
  return (
    <Card
      size="small"
      title={
        whole ? '计划适配分析 · 整个在练计划' : '计划适配分析 · 本节计划课'
      }
      className={styles.planDecisionCard}
    >
      <div className={styles.planDecisionHeader}>
        <div>
          <span className={styles.planDecisionEyebrow}>计划适配性复核</span>
          <Typography.Paragraph
            type="secondary"
            className={styles.planDecisionIntro}
          >
            {whole
              ? '结合会员目标、当前情况、完整排课和近期执行反馈，从五个维度检查这套计划是否合适。'
              : '结合会员资料和本节课的冻结参数，从五个维度检查本节安排是否合适。'}
          </Typography.Paragraph>
          <Typography.Text type="secondary" className={styles.planSafetyHint}>
            仅提供判断依据，不打分、不保证效果，也不会自动修改计划。
          </Typography.Text>
        </div>
        <Space wrap className={styles.planDecisionActions}>
          <Button disabled={current.loading} onClick={() => load(false)}>
            {current.loading
              ? '加载中…'
              : data
              ? '刷新当前结果'
              : '查看已有分析'}
          </Button>
          {data?.taskId && (
            <Button disabled={current.loading} onClick={() => load(true)}>
              查看最新结果
            </Button>
          )}
          {eligible && (
            <Popconfirm
              title={
                current.uncertain
                  ? '使用原请求确认提交结果？不会主动创建新请求。'
                  : '确认按当前资料生成分析？后台将调用模型，不修改计划。'
              }
              onConfirm={submit}
              disabled={current.loading || !!retryPending}
              okText="确认提交"
              cancelText="取消"
            >
              <Button
                type="primary"
                disabled={current.loading || !!retryPending}
              >
                {current.uncertain
                  ? '沿用原请求确认'
                  : data?.status === 'not_found'
                  ? '生成分析'
                  : '重新生成分析'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
      {current.error && (
        <Alert
          className={styles.planDecisionAlert}
          type="warning"
          message={current.error}
        />
      )}
      {current.uncertain && (
        <Typography.Paragraph type="warning">
          提交结果尚未确认。请在本页沿用原请求确认，不要关闭页面后反复新建请求。请求编号：
          {command.current?.requestId}
        </Typography.Paragraph>
      )}
      {data && (
        <div
          className={`${styles.planTaskState} ${
            data.status === 'succeeded'
              ? styles.planTaskSucceeded
              : data.status === 'failed'
              ? styles.planTaskFailed
              : styles.planTaskPending
          }`}
        >
          <strong>{PLAN_TASK_STATES[data.status]}</strong>
          {retryPending && <span>后台将自动重试，请勿重复生成</span>}
        </div>
      )}
      {data?.status === 'failed' && (
        <Alert
          className={styles.planDecisionAlert}
          type="warning"
          message="本次分析未生成有效结论"
          description={`失败分类：${data.failureCode || '未提供'}`}
        />
      )}
      {data?.taskId && (
        <details className={styles.planTaskDetails}>
          <summary>处理信息</summary>
          <Typography.Paragraph type="secondary">
            任务编号：{data.taskId}
            {data.nextExecuteAt
              ? ` · 下次执行：${processingTime(data.nextExecuteAt)}`
              : ''}
          </Typography.Paragraph>
        </details>
      )}
      {data?.assessment && (
        <>
          <div className={styles.planAssessmentMeta}>
            <span>证据截止：{processingTime(data.assessment.capturedAt)}</span>
            <span>
              {whole
                ? `计划覆盖：${data.assessment.coverage.includedCourseCount} / ${data.assessment.coverage.totalPlanCourseCount} 节`
                : '计划覆盖：本节课程'}
            </span>
            <span>模型：{data.model}</span>
          </div>
          {data.assessment.coverage.sourceMissingEvidence.length > 0 && (
            <Alert
              className={styles.planDecisionAlert}
              type="info"
              message="当前证据的覆盖范围有限"
              description={
                <ul>
                  {data.assessment.coverage.sourceMissingEvidence.map(
                    (s, i) => (
                      <li key={i}>{s}</li>
                    ),
                  )}
                </ul>
              }
            />
          )}
          <div className={styles.planDimensionGrid}>
            {Object.keys(PLAN_DIMENSIONS).map((dimension) => {
              const d = data.assessment.dimensions.find(
                (v) => v.dimension === dimension,
              );
              return (
                <section
                  key={dimension}
                  className={`${styles.planDimensionCard} ${
                    d.status === 'CONFLICT'
                      ? styles.planDimensionConflict
                      : d.status === 'CONCERN'
                      ? styles.planDimensionConcern
                      : d.status === 'SUPPORTED'
                      ? styles.planDimensionSupported
                      : styles.planDimensionUnknown
                  }`}
                >
                  <div className={styles.planDimensionHeading}>
                    <strong>{PLAN_DIMENSIONS[dimension]}</strong>
                    <span>{PLAN_FINDING_STATES[d.status]}</span>
                  </div>
                  <Typography.Paragraph
                    className={styles.planDimensionRationale}
                  >
                    {d.rationale}
                  </Typography.Paragraph>
                  {!!d.missingEvidence.length && (
                    <Typography.Paragraph>
                      待补证据：{d.missingEvidence.join('；')}
                    </Typography.Paragraph>
                  )}
                  <Typography.Paragraph>
                    下一核对项：{d.nextCheck}
                  </Typography.Paragraph>
                  <details>
                    <summary>
                      {whole ? '查看判断依据（快照摘录）' : '证据引用编号'}
                    </summary>
                    {whole ? (
                      <ul
                        style={{
                          overflowWrap: 'anywhere',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {d.evidenceRefs.map((ref) => (
                          <li key={ref}>
                            {ref}：
                            {
                              data.assessment.evidenceSources.find(
                                (s) => s.ref === ref,
                              ).excerpt
                            }
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Typography.Text>
                        {d.evidenceRefs.join('、')}
                      </Typography.Text>
                    )}
                  </details>
                </section>
              );
            })}
          </div>
          {whole && (
            <Typography.Paragraph className={styles.planAssessmentBoundary}>
              结论基于生成时冻结的资料。完整排课覆盖不等于完整执行覆盖；缺少视频不等于未训练，证据不足时不会判定为“已适配”。
            </Typography.Paragraph>
          )}
        </>
      )}
    </Card>
  );
}
function message(error) {
  return (
    {
      401: '请先登录后台',
      403: '计划分析请求被服务拒绝，请检查登录状态和服务入口配置',
      429: '提交繁忙或处于冷却期，请稍后沿用原请求重试',
      503: '计划分析暂不可用或尚未启用',
    }[error?.response?.status] || '结果未确认，请查询状态；提交重试请沿用原请求'
  );
}
CurrentPlanAnalysisPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  planId: PropTypes.number.isRequired,
  courseType: PropTypes.number.isRequired,
  courseId: PropTypes.number.isRequired,
};
